import { useState, useRef } from "react";
import { Autocomplete, Badge, Group } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { geoSuggest, geoRetrieve } from "../api";

function newSession() {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

// Mapbox feature types folded to the three labels users understand; place and locality are
// both "a city" from a user's perspective. Finer types (address, street, …) get no badge.
const TYPE_BADGE_KEYS = {
  place: "geo_type_place",
  locality: "geo_type_place",
  region: "geo_type_region",
  country: "geo_type_country",
};

/**
 * Location input with geocoding autocomplete (server-proxied Mapbox, so the token stays server-side).
 * Picking a suggestion yields a canonical GeoLocation (clean localized name + ISO country + coords);
 * typing free text clears it. Emits onChange({ location, geoLocation }) — `location` is the display
 * string to store on candidate.location, `geoLocation` the structured object (or null).
 * `clearable` shows Mantine's clear button (clearing emits { location: "", geoLocation: null });
 * `showTypeBadges` marks each suggestion with its kind (city/region/country) so filter contexts,
 * where picking a region vs. its capital city means different things, are unambiguous.
 */
export default function GeoLocationAutocomplete({
  value, onChange, label, placeholder, clearable = false, showTypeBadges = false, ...rest
}) {
  const { t } = useTranslation("Veft");
  const [options, setOptions] = useState([]);
  const suggestionMap = useRef({});
  const debounceRef = useRef(null);
  const sessionRef = useRef(newSession());
  // True while an option pick is being resolved. Mantine invokes our onOptionSubmit handler
  // FIRST and only then echoes the picked label through onChange — so this flag (set before
  // the first await) suppresses that synchronous echo. Without it, a replacement pick with a
  // filter active first emits { location: label, geoLocation: null }, which consumers rightly
  // treat as free typing: filter cleared, unfiltered search fired — before the retrieve lands.
  const submittingRef = useRef(false);

  function handleChange(val) {
    // A pick is resolving — this change is Mantine's synchronous label echo, not the user
    // typing. The authoritative onChange arrives from the retrieve resolution; emitting (or
    // firing the suggest debounce) here would race it.
    if (submittingRef.current) return;

    // Typing free text drops any previously picked place (it no longer matches what's in the box).
    onChange({ location: val, geoLocation: null });

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) {
      setOptions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await geoSuggest(val.trim(), sessionRef.current);
        const map = {};
        const labels = [];
        for (const s of results) {
          const lbl = s.placeFormatted ? `${s.name}, ${s.placeFormatted}` : s.name;
          map[lbl] = { mapboxId: s.mapboxId, featureType: s.featureType };
          labels.push(lbl);
        }
        suggestionMap.current = map;
        setOptions(labels);
      } catch {
        setOptions([]);
      }
    }, 250);
  }

  // A pick that can't be resolved must NOT leave the picked label in the box as if it were
  // applied — clear the field and say so. (Consumers treat { location: "", geoLocation: null }
  // as a cleared field; for the search page that correctly drops the filter it can't honor.)
  function failPick() {
    onChange({ location: "", geoLocation: null });
    notifications.show({ color: "red", message: t("geo_lookup_failed") });
  }

  async function handleOptionSubmit(val) {
    submittingRef.current = true; // set synchronously, before Mantine's onChange echo (see above)
    try {
      // Yield one microtask so the flag reset in the outer `finally` can never run before
      // Mantine's synchronous echo — the no-mapboxId path below has no other await, and an
      // async function with no await completes (finally included) within the same sync stack.
      await Promise.resolve();
      const mapboxId = suggestionMap.current[val]?.mapboxId;
      if (!mapboxId) {
        // Picked option missing from the map (stale suggestions) — unresolvable, same as a failed retrieve.
        failPick();
        return;
      }
      try {
        const geo = await geoRetrieve(mapboxId, sessionRef.current);
        if (geo) onChange({ location: geo.displayName ?? val, geoLocation: geo });
        else failPick();
      } catch {
        failPick();
      } finally {
        sessionRef.current = newSession(); // a /retrieve ends the billing session
        setOptions([]);
      }
    } finally {
      submittingRef.current = false;
    }
  }

  function renderOptionWithBadge({ option }) {
    const badgeKey = TYPE_BADGE_KEYS[suggestionMap.current[option.value]?.featureType];
    return (
      <Group justify="space-between" wrap="nowrap" gap="xs" style={{ flex: 1 }}>
        <span>{option.value}</span>
        {badgeKey && (
          <Badge size="xs" variant="light" color="gray" style={{ flexShrink: 0 }}>
            {t(badgeKey)}
          </Badge>
        )}
      </Group>
    );
  }

  return (
    <Autocomplete
      label={label ?? t("location")}
      placeholder={placeholder ?? t("location_search_placeholder")}
      value={value}
      onChange={handleChange}
      onOptionSubmit={handleOptionSubmit}
      data={options}
      clearable={clearable}
      renderOption={showTypeBadges ? renderOptionWithBadge : undefined}
      {...rest}
    />
  );
}
